import { Dispatch, SetStateAction, SyntheticEvent, useEffect, useRef, useState, WheelEvent } from "react";
import Icon from "../common/Icon";
import SelectBox from "../common/SelectBox";
import styles from "./PlaceInfo.module.css";
import { placeInfoType } from "../map/floatingsidesheet/FloatingSideSheet";
import { defaultStyle, MainImageType } from "./CreateMap";
import { DataTemplate } from "../map/sidesheet/addplace/AddSinglePlaceInput";
import { isImageFile } from "../common/Common";
import defaultImg from "../../img/ksic_sample_img_00001.jpg";

interface PlaceInfoProps {
    mapData: { row1: string[]; row2: string[] };
    mapSettingData: {
        placeInfo: placeInfoType;
    };
    setMapSettingData: Dispatch<
        SetStateAction<{
            placeInfo: placeInfoType;
        }>
    >;
    setMainImage: Dispatch<SetStateAction<MainImageType>>;
}

/**
 * @author cjs
 * @date 2024-09-27 10:37:23
 * @remark 지도만들기 장소 정보 설정 사용자 설정 컴포넌트
 * @pram props
 * @props PlaceInfoProps
 *      mapData(required) : 마커 설정과 장소 정보 설정에 미리 보여주기 위한 예제 데이터
        mapSettingData(required) : 장소 정보 데이터
        setMapSettingData(required) : 장소 정보 데이터 변경 함수
    @returns PlaceInfo
 */

function PlaceInfo(props: PlaceInfoProps) {
    const selectedId = props.mapSettingData.placeInfo.selectedId;
    const [selectIdx, setSelectIdx] = useState<number>(0); // 셀렉트박스에서 선택한 칼럼의 인덱스 번호
    const [fieldValue, setFieldValue] = useState(true);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const wheelEvent = (event: WheelEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (!scrollRef.current) return;
        scrollRef.current.scrollLeft += event.deltaY;
    };

    useEffect(() => {
        if (selectedId === 0) {
            // FIXME 선택 안함 때문에 index 바뀌어서 지도 장소정보에 이미지 주소 표출
            let _copyMapData = ["선택안함", ...props.mapData.row1];
            let idx = _copyMapData.indexOf(props.mapSettingData.placeInfo.contents[selectedId].fieldName) - 1; // 선택안함 때문에 -1
            setSelectIdx(idx);
        } else {
            let idx = props.mapData.row1.indexOf(props.mapSettingData.placeInfo.contents[selectedId].fieldName);
            setSelectIdx(idx);
        }
        textareaRef.current?.focus();
    }, [selectedId]);

    const clickTemplate = (e: SyntheticEvent) => {
        if (!fieldValue) return;

        let copiedMapSettingData = { ...props.mapSettingData };

        if (e.currentTarget.id === copiedMapSettingData.placeInfo.contents[selectedId].template) {
            copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
        } else {
            copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate[e.currentTarget.id as DataTemplate];
        }
        copiedMapSettingData.placeInfo.contents[selectedId].style = {
            ...defaultStyle,
        };
        props.setMapSettingData(copiedMapSettingData);
    };

    const clickReset = (e: SyntheticEvent) => {
        const resetIcon = e.currentTarget.children;
        if (resetIcon) {
            resetIcon[0].classList.add(styles.rotate);
            setTimeout(() => {
                resetIcon[0].classList.remove(styles.rotate);
            }, 700);
        }
        setFieldValue(true);
        let copiedMapSettingData = { ...props.mapSettingData };

        copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
        copiedMapSettingData.placeInfo.contents[selectedId].style = {
            ...defaultStyle,
        };
        copiedMapSettingData.placeInfo.contents[selectedId].fieldValue = "${fieldValue}";
        props.setMapSettingData(copiedMapSettingData);
    };
    const changeColorPicker = (e: SyntheticEvent) => {
        const target = e.target as HTMLInputElement;
        let copiedMapSettingData = { ...props.mapSettingData };
        copiedMapSettingData.placeInfo.contents[selectedId].style.color = target.value;
        copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
        props.setMapSettingData(copiedMapSettingData);
    };
    const changeFontStyle = (e: SyntheticEvent) => {
        const targetId = e.currentTarget.id;
        let copiedMapSettingData = { ...props.mapSettingData };
        copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
        if (targetId === "bold") {
            if (copiedMapSettingData.placeInfo.contents[selectedId].style.fontWeight === "bold") {
                copiedMapSettingData.placeInfo.contents[selectedId].style.fontWeight = "";
            } else {
                copiedMapSettingData.placeInfo.contents[selectedId].style.fontWeight = "bold";
            }
        } else if (targetId === "italic") {
            if (copiedMapSettingData.placeInfo.contents[selectedId].style.fontStyle === "italic") {
                copiedMapSettingData.placeInfo.contents[selectedId].style.fontStyle = "";
            } else {
                copiedMapSettingData.placeInfo.contents[selectedId].style.fontStyle = "italic";
            }
        } else if (targetId === "underline") {
            if (copiedMapSettingData.placeInfo.contents[selectedId].style.textDecoration === "underline") {
                copiedMapSettingData.placeInfo.contents[selectedId].style.textDecoration = "";
            } else {
                copiedMapSettingData.placeInfo.contents[selectedId].style.textDecoration = "underline";
            }
        }
        props.setMapSettingData(copiedMapSettingData);
    };
    const changefontSort = (e: SyntheticEvent) => {
        const targetId = e.currentTarget.id;
        let copiedMapSettingData = { ...props.mapSettingData };
        copiedMapSettingData.placeInfo.contents[selectedId].style.textAlign = targetId;
        copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
        props.setMapSettingData(copiedMapSettingData);
    };

    const changeFieldValue = () => {
        let copiedMapSettingData = { ...props.mapSettingData };
        if (textareaRef.current) {
            let target = textareaRef.current;
            let targetValue = target.value;
            let selectPosStart = target.selectionStart;
            let selectPosEnd = target.selectionEnd;
            let beforeTxt = targetValue?.substring(0, selectPosStart);
            let afterTxt = targetValue?.substring(selectPosEnd, targetValue.length);
            let idx = targetValue.indexOf(props.mapData.row2[selectIdx]);
            if (idx !== -1) {
                copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;

                let len = props.mapData.row2[selectIdx].length;
                beforeTxt = targetValue?.substring(0, idx);
                afterTxt = targetValue?.substring(idx + len, targetValue.length);
                targetValue = beforeTxt + afterTxt;
                target.selectionStart = idx;
                target.selectionEnd = idx;
                target.focus();
            } else {
                targetValue = beforeTxt + props.mapData.row2[selectIdx] + afterTxt;
                selectPosStart = selectPosStart + props.mapData.row2[selectIdx].length;
                target.selectionStart = selectPosStart;
                target.selectionEnd = selectPosStart;
                target.focus();
            }
            copiedMapSettingData.placeInfo.contents[selectedId].fieldValue = targetValue.replace(props.mapData.row2[selectIdx], "${fieldValue}");
            if (targetValue.includes(props.mapData.row2[selectIdx])) {
                setFieldValue(true);
            } else {
                setFieldValue(false);
            }
        }
        props.setMapSettingData(copiedMapSettingData);
    };
    const selectOption = (n: number) => {
        setSelectIdx(n);
        let copiedMapSettingData = { ...props.mapSettingData };
        if (selectedId === 0) {
            // 이미지 선택 처리 부분

            // 선택 안함 또는 선택한 인덱스가 이미지 필드가 아닌 경우 판단
            const checkImg = n === 0 ? false : isImageFile(props.mapData.row2[n - 1]);

            // 선택한 인덱스가 이미지 필드인 경우
            if (checkImg) {
                props.setMainImage({ isMainImage: true, mainImageIdx: n - 1, mainimageUrl: props.mapData.row2[n - 1] }); // 선택한 인덱스가 이미지 필드인 경우
            } else {
                props.setMainImage({ isMainImage: false, mainImageIdx: -1, mainimageUrl: "" }); // 선택한 인덱스가 이미지 필드가 아닌 경우
            }
        } else {
            copiedMapSettingData.placeInfo.contents[selectedId].fieldName = props.mapData.row1[n];
        }
        props.setMapSettingData(copiedMapSettingData);
    };
    useEffect(() => {
        handleContentValue();
    }, [props.mapSettingData]);
    const handleContentValue = () => {
        if (props.mapSettingData.placeInfo.contents[selectedId].fieldValue.includes("${fieldValue}")) {
            setFieldValue(true);
        } else {
            if (fieldValue) {
                let copiedMapSettingData = { ...props.mapSettingData };
                copiedMapSettingData.placeInfo.contents[selectedId].template = DataTemplate.none;
                props.setMapSettingData(copiedMapSettingData);
            }
            setFieldValue(false);
        }
    };
    const changeDataToFieldValue = (s: string, fieldName: string) => {
        let fieldNameIdx = props.mapData.row1.indexOf(fieldName);
        if (fieldNameIdx != null) {
            return s.replace(props.mapData.row2[fieldNameIdx], "${fieldValue}");
        }
        return s;
    };

    const handleTextValue = (e: SyntheticEvent) => {
        const textValue = e.currentTarget as HTMLTextAreaElement;
        let copiedMapSettingData = { ...props.mapSettingData };
        copiedMapSettingData.placeInfo.contents[selectedId].fieldValue = changeDataToFieldValue(
            textValue.value,
            copiedMapSettingData.placeInfo.contents[selectedId].fieldName
        );
        props.setMapSettingData(copiedMapSettingData);
    };
    return (
        <>
            {selectedId === 0 ? (
                <div className={styles.wrapFieldType}>
                    <div className={styles.settingHead}>메인 이미지 필드</div>
                    <div className={styles.fieldTypeSelectBox}>
                        <SelectBox optionList={["선택안함", ...props.mapData.row1]} selected={selectIdx} selectOption={selectOption} />
                    </div>
                </div>
            ) : (
                <>
                    <div className={styles.wrapFieldType}>
                        <div className={styles.settingHead}>필드 종류</div>
                        <div className={styles.fieldTypeSelectBox}>
                            <SelectBox optionList={props.mapData.row1} selected={selectIdx} selectOption={selectOption} />
                        </div>
                    </div>
                    <div className={styles.wrapTemplate}>
                        <div className={styles.settingHead}>템플릿</div>
                        <div className={styles.wrapTemplateItem}>
                            <button
                                id="link"
                                className={`${styles.button} ${styles.templateItem} ${
                                    props.mapSettingData.placeInfo.contents[selectedId].template === "link" ? styles.select : ""
                                }`}
                                onClick={clickTemplate}
                            >
                                <Icon
                                    name={"link"}
                                    color={props.mapSettingData.placeInfo.contents[selectedId].template === "link" ? "white" : "none"}
                                    size={"medium"}
                                />
                            </button>
                            <button
                                id="call"
                                className={`${styles.button} ${styles.templateItem} ${
                                    props.mapSettingData.placeInfo.contents[selectedId].template === "call" ? styles.select : ""
                                }`}
                                onClick={clickTemplate}
                            >
                                <Icon
                                    name={"call"}
                                    color={props.mapSettingData.placeInfo.contents[selectedId].template === "call" ? "white" : "none"}
                                    size={"medium"}
                                />
                            </button>
                            <button
                                id="attach_money"
                                className={`${styles.button} ${styles.templateItem} ${
                                    props.mapSettingData.placeInfo.contents[selectedId].template === "attach_money" ? styles.select : ""
                                }`}
                                onClick={clickTemplate}
                            >
                                <Icon
                                    name={"attach_money"}
                                    color={props.mapSettingData.placeInfo.contents[selectedId].template === "attach_money" ? "white" : "none"}
                                    size={"medium"}
                                />
                            </button>
                            <button
                                id="format_quote"
                                className={`${styles.button} ${styles.templateItem} ${
                                    props.mapSettingData.placeInfo.contents[selectedId].template === "format_quote" ? styles.select : ""
                                }`}
                                onClick={clickTemplate}
                            >
                                <Icon
                                    name={"format_quote"}
                                    color={props.mapSettingData.placeInfo.contents[selectedId].template === "format_quote" ? "white" : "none"}
                                    size={"medium"}
                                />
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className={styles.placeInfoHeader}>
                            <div className={styles.settingHead}>장소정보</div>
                            <button id="resetButton" className={`${styles.button} ${styles.resetBtn}`} onClick={clickReset}>
                                <Icon name={"reset"} color={"none"} size={"medium"} />
                            </button>
                        </div>
                        <div className={styles.placeInfoContent}>
                            <div className={styles.textEditorBtn} onWheel={wheelEvent} ref={scrollRef}>
                                <div className={styles.fontStyle}>
                                    <button id="bold" className={`${styles.editorBtn}`} onClick={changeFontStyle}>
                                        <Icon
                                            name={"formatBold"}
                                            color={props.mapSettingData.placeInfo.contents[selectedId].style.fontWeight === "" ? "none" : "default"}
                                            size={"medium"}
                                        />
                                    </button>
                                    <button id="italic" className={`${styles.editorBtn}`} onClick={changeFontStyle}>
                                        <Icon
                                            name={"formatItalic"}
                                            color={props.mapSettingData.placeInfo.contents[selectedId].style.fontStyle === "" ? "none" : "default"}
                                            size={"medium"}
                                        />
                                    </button>
                                    <button id="underline" className={`${styles.editorBtn}`} onClick={changeFontStyle}>
                                        <Icon
                                            name={"formatUnderlined"}
                                            color={
                                                props.mapSettingData.placeInfo.contents[selectedId].style.textDecoration === "" ? "none" : "default"
                                            }
                                            size={"medium"}
                                        />
                                    </button>
                                </div>
                                <div className={styles.fontColor}>
                                    <label
                                        htmlFor="colorPicker"
                                        className={`${styles.editorBtn} ${styles.colorPickerIcon}`}
                                        style={{ backgroundColor: props.mapSettingData.placeInfo.contents[selectedId].style.color }}
                                    ></label>
                                    <input type="color" id="colorPicker" className={styles.colorPicker} onChange={changeColorPicker}></input>
                                </div>
                                <div className={styles.fontSort}>
                                    <button id="left" className={`${styles.editorBtn}`} onClick={changefontSort}>
                                        <Icon
                                            name={"formatAlignLeft"}
                                            color={
                                                props.mapSettingData.placeInfo.contents[selectedId].style.textAlign === "left" ? "default" : "none"
                                            }
                                            size={"medium"}
                                        />
                                    </button>
                                    <button id="center" className={`${styles.editorBtn}`} onClick={changefontSort}>
                                        <Icon
                                            name={"formatAlignCenter"}
                                            color={
                                                props.mapSettingData.placeInfo.contents[selectedId].style.textAlign === "center" ? "default" : "none"
                                            }
                                            size={"medium"}
                                        />
                                    </button>
                                    <button id="right" className={`${styles.editorBtn}`} onClick={changefontSort}>
                                        <Icon
                                            name={"formatAlignRight"}
                                            color={
                                                props.mapSettingData.placeInfo.contents[selectedId].style.textAlign === "right" ? "default" : "none"
                                            }
                                            size={"medium"}
                                        />
                                    </button>
                                </div>
                                <div className={styles.fieldValue}>
                                    <button
                                        className={`${styles.editorBtn}`}
                                        onClick={changeFieldValue}
                                        style={{ color: `${fieldValue ? "rgb(0, 136, 255)" : "#666"}` }}
                                    >
                                        <Icon name={"frameSource"} color={fieldValue ? "default" : "none"} size={"medium"} />
                                        필드값
                                    </button>
                                </div>
                            </div>

                            <div className={styles.wrapTextEditor}>
                                <textarea
                                    ref={textareaRef}
                                    className={styles.textEditor}
                                    spellCheck="false"
                                    onChange={handleTextValue}
                                    value={props.mapSettingData.placeInfo.contents[selectedId].fieldValue.replace(
                                        "${fieldValue}",
                                        props.mapData.row2[selectIdx]
                                    )}
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
export default PlaceInfo;
